import { FormControl, FormErrorMessage, Select } from "@chakra-ui/react";
import { DEFAULT_LANGUAGE, Language, translate as t } from "@models/language";
import { isEmpty } from "lodash";
import {
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";
import DynamicFormFieldHeader from "./DynamicFormFieldHeader";
import { Field, UIFormField } from "./types";

const DynamicFormSelect = ({
  field,
  errors,
  register,
  language: language = DEFAULT_LANGUAGE,
  isDirty: isDirty = true,
  className: className = "",
}: {
  field: Field;
  errors: FieldErrors<FieldValues>;
  register: UseFormRegister<FieldValues>;
  language?: Language;
  isDirty?: boolean;
  className?: string;
}) => {
  // TODO: default translation
  const _t = (key?: string, language = DEFAULT_LANGUAGE) => key || "";

  // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
  let colSpan = "sm:col-span-1";
  switch (field.span) {
    case 1:
      colSpan = "sm:col-span-1";
      break;
    case 2:
      colSpan = "sm:col-span-2";
      break;
    case 3:
      colSpan = "sm:col-span-3";
      break;
    case 4:
      colSpan = "sm:col-span-4";
      break;
  }

  const toRegisterOptions = (
    field: UIFormField
  ): RegisterOptions<FieldValues, string> => {
    return {
      ...(field.required
        ? {
            required:
              t(field.errorMessages?.required, language) ||
              _t("This field is required", language),
          }
        : {}),
    };
  };

  if (!field.options || isEmpty(field.options)) return <></>;
  return (
    <>
      <FormControl
        isInvalid={isDirty && !!errors[field.key]}
        className={`${field.span ? colSpan : ""} col-span-1 ${className}`}
        isRequired={field.required}
        isDisabled={field.disabled}
        isReadOnly={field.readonly}
      >
        <div
          onMouseEnter={() => {
            if (field?.updateCurrentEventPreviewPart) {
              field?.updateCurrentEventPreviewPart(field.key);
            }
          }}
          onMouseLeave={() => {
            if (field?.updateCurrentEventPreviewPart) {
              field?.updateCurrentEventPreviewPart("DUMMY");
            }
          }}
        >
          <DynamicFormFieldHeader field={field} language={language} />
          <Select
            pointerEvents={field.readonly ? "none" : "auto"}
            id={field.key}
            placeholder={
              t(field.placeholder, language) || _t("Please select", language)
            }
            defaultValue={
              (
                field.options.find((option) => option.selected) ||
                field.options[0]
              ).value
            }
            {...register(field.key, {
              ...toRegisterOptions(field),
            })}
          >
            {field.options.map((option, i) => (
              <option key={i} value={option.value} disabled={option.disabled}>
                {t(option.label, language) || option.value}
              </option>
            ))}
          </Select>
          <FormErrorMessage>
            {String(errors[field.key]?.message || "")}
          </FormErrorMessage>
        </div>
      </FormControl>
    </>
  );
};

export default DynamicFormSelect;
